import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../hooks/useAxios";
import { useFeedBack } from "../../../context/FeedBackContext";
import handleLoadSelectOptions from "../../../util/handleLoadSelectOptions";
import mapValues from "../../../util/mapValues";
import useZonesBi from "../../../hooks/useZonesBi";
import useAreas from "../../../hooks/useAreas";
import ImgUploadInput from "../../../components/ImgUploadInput";
import AsyncSelect from 'react-select/async';
import useUsers from "../../../hooks/useUsers";
import { Image } from "react-bootstrap";
import profileImg from "../../../assets/images/profile.png";
import { AiFillCheckCircle } from "react-icons/ai";
import useCostCenters from "../../../hooks/useCostCenters";
import useStatuses from "../../../hooks/useStatuses";

const PowerbisEdit = () => {

    const { id } = useParams();

    const entity = {
        name: 'Power Bi',
        url: 'power-bi',
        frontendUrl: '/power-bi',
        camelName: 'powerBi',
    };

    const navigate = useNavigate();

    const [data, setData] = useState({
        title: '',
        url: '',
        workspaceId: '',
        reportId: '',
        datasetId: '',
        integrationType: 'api',
        zone: '',
        status: ''
    });

    const [filters, setFilters] = useState({
        page: 1,
        perPage: 12,
        search: '',
        zoneId: '',
        costCenterId: '',
        userStatus: 'activos'
    });

    const { setLoading, setCustomAlert } = useFeedBack();

    const [{ data: dataToUpdate, loading: loadingDataToUpdate }, getRecord] = useAxios({ url: `/${entity?.url}/${id}` }, { useCache: false });

    const [{ data: updateData, loading }, updateRecord] = useAxios({ url: `/${entity?.url}/${id}`, method: 'POST' }, { manual: true, useCache: false });

    const [{ zones, loading: zonesLoading }, getZones] = useZonesBi({ params: { perPage: 50 } }, { useCache: false });

    const [{ areas, loading: areasLoading }, getAreas] = useAreas({ params: { perPage: 50 } }, { useCache: false });

    const [{ statuses, loading: statusesLoading }, getStatuses] = useStatuses({ params: { perPage: 50 } }, { useCache: false });

    const [{ users, total, numberOfPages, loading: loadingUsers }, getUsers] = useUsers({ params: { ...filters }, options: { useCache: false } });

    const [{ costCenters, loading: costCentersLoading }, getCostCenters] = useCostCenters({ params: { perPage: 50, orderBy: 'name ASC' } }, { useCache: false });

    const [{ data: latestViewsData, loading: loadingLatestViews }] = useAxios({ url: '/power-bi-views', params: { powerbiId: id, latestPerUser: 1, perPage: 50 } }, { useCache: false });

    const [{ data: excelLatestData, loading: loadingExcelLatest }, getExcelLatest] = useAxios({ url: '/power-bi-views/export/excel', method: 'GET', responseType: 'blob' }, { manual: true, useCache: false });

    useEffect(() => {
        if (excelLatestData) {
            const blob = new Blob([excelLatestData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const excelBlobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = excelBlobUrl;
            link.setAttribute('download', `accesos_powerbi_${id}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(excelBlobUrl);
        }
    }, [excelLatestData, id]);

    const [currentUsers, setCurrentUsers] = useState([]);

    useEffect(() => {
        if (users) {
            setCurrentUsers((oldUsers) => {
                return [...oldUsers, ...users]
            })
        }
    }, [users])

    useEffect(() => {
        if (dataToUpdate) {

            const { createdAt, imagePath, zone, status, id, area, workspaceId, reportId, datasetId, isEmbedded, ...rest } = dataToUpdate?.data;

            setData((oldData) => {
                return {
                    ...oldData,
                    ...rest,
                    workspaceId: workspaceId || '',
                    reportId: reportId || '',
                    datasetId: datasetId || '',
                    integrationType: (isEmbedded || (workspaceId && reportId)) ? 'api' : 'url',
                    zone: zone ? { label: zone?.name, value: zone?.id } : null,
                    area: area ? { label: area?.name, value: area?.id } : null,
                    status: { label: status?.name, value: status?.id }
                }
            });
        }
    }, [dataToUpdate]);

    useEffect(() => {
        setLoading({
            show: loading,
            message: 'Actualizando el registro'
        })
    }, [loading]);

    useEffect(() => {
        setLoading({
            show: loadingDataToUpdate,
            message: 'Obteniendo información'
        });
    }, [loadingDataToUpdate]);

    useEffect(() => {
        if (updateData) {
            setCustomAlert({
                show: true,
                severity: 'success',
                title: 'Operación Exitosa',
                message: 'El registro fue actualizado exitosamente.'
            });
            navigate(`${entity?.frontendUrl}/listar`);
        }
    }, [updateData])

    const handleChange = (e) => {
        setData((oldData) => {
            return {
                ...oldData,
                [e.target.name]: e.target?.type === 'file' ? e.target?.files : e.target.value
            }
        });
    }

    const handleExtractFromUrl = (urlToParse) => {
        if (!urlToParse) return;
        const workspaceMatch = urlToParse.match(/groups\/([a-fA-F0-9\-]{36})/i) || urlToParse.match(/groups\/([^\/]+)/i);
        const reportMatch = urlToParse.match(/reports\/([a-fA-F0-9\-]{36})/i) || urlToParse.match(/reports\/([^\/]+)/i);

        const extractedWorkspaceId = workspaceMatch ? workspaceMatch[1] : '';
        const extractedReportId = reportMatch ? reportMatch[1] : '';

        if (extractedWorkspaceId || extractedReportId) {
            setData((oldData) => ({
                ...oldData,
                workspaceId: extractedWorkspaceId || oldData.workspaceId,
                reportId: extractedReportId || oldData.reportId
            }));
        }
    };

    const handleFilters = (e) => {
        setCurrentUsers([]);
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                [e.target.name]: e.target.value,
                page: 1
            }
        })
    }

    const handleAddPage = () => {
        setFilters((oldFilters) => {
            return {
                ...oldFilters,
                page: oldFilters?.page + 1
            }
        })
    }

    const handleUser = (userId) => {
        const haveValue = data?.userIds?.includes(userId);
        var newValues = [];
        if (haveValue) newValues = data?.userIds?.filter(value => value !== userId);
        if (!haveValue) newValues = [...data?.userIds, userId];

        setData((oldData) => {
            return {
                ...oldData,
                userIds: newValues
            }
        });
    }

    const handleSubmit = (e) => {
        e?.preventDefault();
        const formData = new FormData();

        Object.keys(data).map((key, i) => {
            if (data[key]) {
                if (key === 'zone' && data[key]?.value) {
                    formData.append('zoneId', data[key]?.value);
                    return;
                }

                if (key === 'area' && data[key]?.value) {
                    formData.append('areaId', data[key]?.value);
                    return;
                }

                if (key === 'status' && data[key]?.value) {
                    formData.append('statusId', data[key]?.value);
                    return;
                }

                if (key === 'image') {
                    formData.append(key, data[key][0], data[key]?.[0]?.name);
                    return;
                }

                if (key === 'userIds') {
                    data[key]?.forEach((userId, i) => {
                        formData.append(`${key}[${i}]`, userId);
                    });
                    return;
                }
                formData.append(key, data[key]);
            }
        });

        formData.append('_method', 'PUT');

        updateRecord({ data: formData });
    }

    return (
        <div>
            <div className="my-4 align-items-center justify-content-between d-flex">
                <h3>
                    Editar {entity?.name}
                </h3>
                <Link to={`${entity?.frontendUrl}/listar`} className="btn btn-primary">
                    Volver al listado
                </Link>
            </div>

            <form className="card p-4" onSubmit={handleSubmit}>
                <div className="row align-items-start">
                    <div className="col-md-6 mb-3">
                        <div className="mb-4">
                            <label className="text-primary fw-bold mb-2">Imagen del Power BI</label>
                            <ImgUploadInput
                                previewImage={dataToUpdate?.data?.imagePath}
                                name="image"
                                style={{
                                    boxShadow: "0rem 0.3125rem 0.3125rem 0rem rgba(82, 63, 105, 0.05)"
                                }}
                                imageStyle={{
                                    borderRadius: 10,
                                    boxShadow: "0rem 0.3125rem 0.3125rem 0rem rgba(82, 63, 105, 0.05)"
                                }}
                                change={handleChange}
                            />
                        </div>

                        <div className="mb-3">
                            <label className="text-primary fw-bold mb-2">Tipo de Integración Power BI</label>
                            <div className="d-flex align-items-center mb-3">
                                <div className="form-check me-4">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="integrationType"
                                        id="editIntegrationTypeApi"
                                        value="api"
                                        checked={data?.integrationType === 'api'}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label fw-bold" htmlFor="editIntegrationTypeApi">
                                        API Embedded (Seguro)
                                    </label>
                                </div>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="integrationType"
                                        id="editIntegrationTypeUrl"
                                        value="url"
                                        checked={data?.integrationType === 'url'}
                                        onChange={handleChange}
                                    />
                                    <label className="form-check-label" htmlFor="editIntegrationTypeUrl">
                                        URL Pública (Legacy)
                                    </label>
                                </div>
                            </div>

                            {data?.integrationType === 'api' ? (
                                <div>
                                    <label className="text-primary fw-bold mb-1" style={{ fontSize: '13px' }}>
                                        🔗 Auto-extraer IDs desde la URL de Power BI
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control mb-1"
                                        placeholder="Pegue aquí la URL (ej: https://app.powerbi.com/groups/.../reports/...)"
                                        onChange={(e) => handleExtractFromUrl(e.target.value)}
                                    />
                                    <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                                        Pegue la URL completa para rellenar <strong>Workspace ID</strong> y <strong>Report ID</strong> automáticamente.
                                    </small>
                                </div>
                            ) : (
                                <div>
                                    <label className="text-primary fw-bold mb-1">
                                        Url Pública <small className="text-danger">*</small>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        onChange={handleChange}
                                        name="url"
                                        value={data?.url}
                                        placeholder="https://app.powerbi.com/view?r=..."
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-md-6 mb-3">
                        <div className="form-group mb-3">
                            <label className="text-primary fw-bold">
                                Título <small className="text-danger">*</small>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                onChange={handleChange}
                                name="title"
                                value={data?.title}
                                placeholder="Título del Power BI"
                            />
                        </div>

                        {data?.integrationType === 'api' && (
                            <>
                                <div className="row">
                                    <div className="col-md-6 form-group mb-3">
                                        <label className="text-primary fw-bold">
                                            Workspace ID <small className="text-danger">*</small>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            onChange={handleChange}
                                            name="workspaceId"
                                            value={data?.workspaceId}
                                            placeholder="ej: xxxxxxxx-xxxx..."
                                        />
                                    </div>
                                    <div className="col-md-6 form-group mb-3">
                                        <label className="text-primary fw-bold">
                                            Report ID <small className="text-danger">*</small>
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            onChange={handleChange}
                                            name="reportId"
                                            value={data?.reportId}
                                            placeholder="ej: xxxxxxxx-xxxx..."
                                        />
                                    </div>
                                </div>
                                <div className="form-group mb-3">
                                    <label className="text-muted">
                                        Dataset ID <small className="text-secondary">(Opcional)</small>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        onChange={handleChange}
                                        name="datasetId"
                                        value={data?.datasetId}
                                        placeholder="ej: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                    />
                                </div>
                            </>
                        )}

                        <div className="form-group mb-3">
                            <label>Área<span className="text-danger">*</span></label>
                            <AsyncSelect
                                isClearable
                                onFocus={() => { getAreas() }}
                                value={data?.area}
                                isLoading={areasLoading}
                                defaultOptions={mapValues(areas)}
                                name="area"
                                loadOptions={(e) => handleLoadSelectOptions(e, getAreas)}
                                placeholder='Escriba el nombre para buscar...'
                                onChange={(e) => { handleChange({ target: { value: e, name: 'area' } }) }}
                            />
                        </div>
                        <div className="row">
                            <div className="col-md-6 form-group mb-3">
                                <label>Zona<span className="text-danger">*</span></label>
                                <AsyncSelect
                                    isClearable
                                    onFocus={() => { getZones() }}
                                    value={data?.zone}
                                    isLoading={zonesLoading}
                                    defaultOptions={mapValues(zones)}
                                    name="zone"
                                    loadOptions={(e) => handleLoadSelectOptions(e, getZones)}
                                    placeholder='Escriba el nombre para buscar...'
                                    onChange={(e) => { handleChange({ target: { value: e, name: 'zone' } }) }}
                                />
                            </div>
                            <div className="col-md-6 form-group mb-3">
                                <label>Estado<span className="text-danger">*</span></label>
                                <AsyncSelect
                                    isClearable
                                    onFocus={() => { getStatuses() }}
                                    value={data?.status}
                                    isLoading={statusesLoading}
                                    defaultOptions={mapValues(statuses)}
                                    name="status"
                                    loadOptions={(e) => handleLoadSelectOptions(e, getStatuses)}
                                    placeholder='Escriba el nombre para buscar...'
                                    onChange={(e) => { handleChange({ target: { value: e, name: 'status' } }) }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-12 mb-5">
                    <h2>👨‍👦‍👦 Usuarios con acceso:</h2>
                    <small>Por favor seleccione los usuarios con acceso a este powerBi:</small>
                    <br />
                    <br />
                    <div className="row align-items-center">
                        <div className="col-md-12 mb-3">
                            <div className="row align-items-center">
                                <div className="col-md-3 mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={filters?.search}
                                        onChange={handleFilters}
                                        name="search"
                                        placeholder="Buscar..."
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <AsyncSelect
                                        isClearable
                                        onFocus={() => { getCostCenters() }}
                                        isLoading={costCentersLoading}
                                        defaultOptions={mapValues(costCenters)}
                                        name="costCenterId"
                                        loadOptions={(e) => handleLoadSelectOptions(e, getCostCenters, { orderBy: 'name ASC' })}
                                        placeholder='Escriba el nombre para buscar...'
                                        onChange={(e) => {
                                            handleFilters({ target: { value: e?.value, name: 'costCenterId' } })
                                        }}
                                    />
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="d-flex align-items-center">
                                        <span className="d-none d-md-block" style={{ marginRight: 10 }}>Mostrar:</span>
                                        <select
                                            name="perPage"
                                            onChange={handleFilters}
                                            value={filters?.perPage}
                                            className="form-control"
                                            style={{ maxWidth: "50%" }}
                                        >
                                            <option value="12">12</option>
                                            <option value="20">20</option>
                                            <option value="50">50</option>
                                            <option value="100">100</option>
                                            <option value="200">200</option>
                                            <option value="500">500</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <div className="btn-group w-100" role="group">
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${filters?.hasPowerBis ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setCurrentUsers([]);
                                                setFilters((oldFilters) => ({
                                                    ...oldFilters,
                                                    page: 1,
                                                    hasPowerBis: oldFilters?.hasPowerBis ? null : [id],
                                                    hasNotPowerBis: null
                                                }));
                                            }}
                                        >
                                            Con acceso
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${filters?.hasNotPowerBis ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setCurrentUsers([]);
                                                setFilters((oldFilters) => ({
                                                    ...oldFilters,
                                                    page: 1,
                                                    hasNotPowerBis: oldFilters?.hasNotPowerBis ? null : [id],
                                                    hasPowerBis: null
                                                }));
                                            }}
                                        >
                                            Sin acceso
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${!filters?.hasNotPowerBis && !filters?.hasPowerBis ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setCurrentUsers([]);
                                                setFilters((oldFilters) => ({
                                                    ...oldFilters,
                                                    page: 1,
                                                    hasNotPowerBis: null,
                                                    hasPowerBis: null
                                                }));
                                            }}
                                        >
                                            Todos
                                        </button>
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label d-block text-muted mb-1" style={{ fontSize: '12px' }}>
                                        Filtro por Estado del Usuario:
                                    </label>
                                    <div className="btn-group w-100" role="group">
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${filters?.userStatus === 'activos' || !filters?.userStatus ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setCurrentUsers([]);
                                                setFilters((oldFilters) => ({
                                                    ...oldFilters,
                                                    page: 1,
                                                    userStatus: 'activos'
                                                }));
                                            }}
                                        >
                                            Activos
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${filters?.userStatus === 'desvinculados' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setCurrentUsers([]);
                                                setFilters((oldFilters) => ({
                                                    ...oldFilters,
                                                    page: 1,
                                                    userStatus: 'desvinculados'
                                                }));
                                            }}
                                        >
                                            Desvinculados
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn btn-xs ${filters?.userStatus === 'todos' ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setCurrentUsers([]);
                                                setFilters((oldFilters) => ({
                                                    ...oldFilters,
                                                    page: 1,
                                                    userStatus: 'todos'
                                                }));
                                            }}
                                        >
                                            Todos
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <ul className="custom-scrollbar scrollbar-primary mt-3 row align-items-center" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        {
                            currentUsers?.length > 0 ?
                                currentUsers?.map((user, i) => {
                                    return (
                                        <li
                                            key={i}
                                            onClick={(e) => handleUser(user?.id)}
                                            className="mb-3 px-3 col-md-6 col-lg-4"
                                        >
                                            <div
                                                className="d-flex custom-responsible-option p-2 border-primary"
                                                style={{
                                                    borderLeft: '1px solid',
                                                    alignItems: 'center',
                                                    minHeight: 80,
                                                    cursor: 'pointer',
                                                    justifyContent: 'space-between',
                                                    boxShadow: "0rem 0.3125rem 0.3125rem 0rem rgba(82, 63, 105, 0.05)"
                                                }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <p className="text-primary" style={{ marginRight: 10, marginBottom: 0 }}>
                                                        {user?.id}.-
                                                    </p>
                                                    <Image
                                                        style={{ height: 40, width: 40, marginRight: 5 }}
                                                        src={user?.imagePath || profileImg}
                                                        roundedCircle
                                                    />
                                                    <div>
                                                        <h5 className="m-0">
                                                            {user?.name} - ({user?.email})
                                                        </h5>
                                                        {user?.deletedAt && (
                                                            <span className="badge bg-danger text-white mb-1" style={{ fontSize: '10px' }}>
                                                                Desvinculado
                                                            </span>
                                                        )}
                                                        <p className="m-0">
                                                            Rut: {user?.documentNumber}
                                                        </p>
                                                        <p className="m-0 text-primary">
                                                            {user?.costCenter?.name}
                                                        </p>
                                                    </div>
                                                </div>
                                                {
                                                    data?.userIds?.includes(user?.id) &&
                                                    <div>
                                                        <AiFillCheckCircle className="text-primary" style={{ fontSize: 22 }} />
                                                    </div>
                                                }
                                            </div>
                                        </li>
                                    )
                                })
                                :
                                <li className="text-center">
                                    No se encontrarón resultados
                                </li>
                        }
                        {
                            loadingUsers &&
                            <li>
                                <div className="spinner">
                                    <div className="double-bounce1 bg-primary"></div>
                                    <div className="double-bounce2 bg-primary"></div>
                                </div>
                            </li>
                        }
                        {
                            numberOfPages > filters?.page && !loadingUsers ?
                                <li className="text-center">
                                    <button type="button" onClick={handleAddPage} className="btn btn-xs btn-primary" >
                                        Cargar mas
                                    </button>
                                </li>
                                :
                                null
                        }
                    </ul>
                </div>

                <div className="col-md-12 mt-5 mb-4">
                    <div className="border-top pt-4">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <h3 className="mb-0 text-primary d-flex align-items-center">
                                <span style={{ marginRight: 8 }}>👁️</span> Últimos accesos de usuarios
                            </h3>
                            <button
                                type="button"
                                className="btn btn-success btn-sm"
                                onClick={() => getExcelLatest({ params: { powerbiId: id, latestPerUser: 1 } })}
                                disabled={loadingExcelLatest}
                            >
                                {loadingExcelLatest ? 'Descargando...' : 'Descargar Excel de Accesos'}
                            </button>
                        </div>
                        <small className="text-muted d-block mb-3">
                            Muestra el último registro de ingreso de cada usuario a este Power BI:
                        </small>

                        {loadingLatestViews ? (
                            <div className="text-center py-4">
                                <div className="spinner">
                                    <div className="double-bounce1 bg-primary"></div>
                                    <div className="double-bounce2 bg-primary"></div>
                                </div>
                            </div>
                        ) : (latestViewsData?.data?.length > 0) ? (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle border">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Usuario</th>
                                            <th>Rut / Documento</th>
                                            <th>Centro de Costo</th>
                                            <th>Estado</th>
                                            <th>Último Acceso</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {latestViewsData.data.map((item, index) => {
                                            const u = item?.user;
                                            return (
                                                <tr key={item?.id || index}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Image
                                                                style={{ height: 32, width: 32, marginRight: 8 }}
                                                                src={u?.imagePath || profileImg}
                                                                roundedCircle
                                                            />
                                                            <div>
                                                                <strong>{u?.name || '---'}</strong>
                                                                <div className="small text-muted">{u?.email}</div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{u?.documentNumber || '---'}</td>
                                                    <td>{u?.costCenter?.name || '---'}</td>
                                                    <td>
                                                        {u?.deletedAt ? (
                                                            <span className="badge bg-danger text-white">Desvinculado</span>
                                                        ) : (
                                                            <span className="badge bg-success text-white">Activo</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border">
                                                            📅 {item?.createdAt || '---'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="alert alert-light text-center border">
                                Aún no hay accesos registrados para este Power BI.
                            </div>
                        )}
                    </div>
                </div>

                <div className="text-center">
                    <button className="btn btn-block btn-primary">
                        Actualizar
                    </button>
                </div>
            </form>
        </div>
    )
}

export default PowerbisEdit;